const LOCAL_STORAGE_WISHLIST_KEY = 'shopify-wishlist';
const BUTTON_ACTIVE_CLASS = 'active';
const GRID_LOADED_CLASS = 'loaded';
const isMobile = window.innerWidth <= 768;

const selectors = {
  button: '.wishlist-icon',
  grid: '.page-wishlist-container__items',
  productCard: '.page-wishlist-container__items__product-card',
  menuIconEmpty: '.site-header .wishlist-container .wishlist-icon-menu-empty',
  menuIconFull: '.site-header .wishlist-container .wishlist-icon-menu-full',
  menuIconHeader: '.site-header .wishlist-container .wishlist-header-icon',
  addToCartButtons: '.page-wishlist-container .btn-add-to-cart',
  boxSizes: '.page-wishlist-container .submit-radio',
  noSizes: '.page-wishlist-container .no-size-add',
  wishListContainer: '.page-wishlist-container',
};

/**
 * Initializes and updates the wishlist functionality.
 *
 * - Initializes buttons.
 * - Updates the menu icon header.
 * - Handles the menu icon header click event.
 * - Sets up the wishlist grid if the wishlist container is present.
 *
 * @returns {void}
 */
export const wishList = () => {
  initButtons();
  updateMenuIconHeader();
  handleMenuIconHeader();
  if (document.querySelector(selectors.wishListContainer)) {
    setupGrid()
    .then(() => {
      showSizes();
      handleBoxSizes();
      handleNosizes();
    })
    .catch((err) => console.error('Failed SetupGrid', err));
  }
  document.addEventListener('newitems', (event)=> {
    const { items } = event.detail;
    items.forEach(item => {
      initButtons(item);
    });
  })
  window.addEventListener('load', () => {
    if (document.body.classList.contains('template-collection')) {
      observeInfiniteScrollPLP();
    }
  });
}

/**
 * Fetches HTML content for a product card.
 *
 * @param {Object} productInfo - Information about the product.
 * @param {string} productInfo.handle - The handle of the product.
 * @param {string} productInfo.variantId - The ID of the product variant.
 * @returns {Promise<string>} - HTML content for the product card.
 */
const fetchProductCardHTML = async (productInfo) => {
  const productTileTemplateUrl = `/products/${productInfo.handle}?variant=${productInfo.variantId}&view=wishlist`;
  console.log('productTileTemplateUrl', productTileTemplateUrl);
  try {
    const response = await fetch(productTileTemplateUrl);
    if (!response.ok) throw new Error(`Network response was not ok for URL: ${productTileTemplateUrl}`);
    const text = await response.text();
    const parser = new DOMParser();
    const htmlDocument = parser.parseFromString(text, 'text/html');
    console.log('htmlDocument', htmlDocument.documentElement);
    const productCard = htmlDocument.documentElement.querySelector(selectors.productCard);
    if (!productCard) throw new Error(`Product card not found in the fetched content for handle: ${productInfo.handle}`);
    return productCard.outerHTML;
  } catch (err) {
    return console.error(`Failed to load content for handle: ${productInfo.handle}`, err);
  }
};

/**
 * Sets up the product grid with wishlist items.
 *
 * @param {HTMLElement} grid - The grid element to populate with wishlist items.
 * @returns {void}
 */
const setupGrid = async () => {
  const grid = document.querySelector(selectors.grid) || false;
  if ( !grid ) {
    return Promise.reject('Grid container not found');
  }
  try {
    const wishlist = getWishlist();
    const requests = wishlist.map(fetchProductCardHTML);
    const responses = await Promise.all(requests);
    const wishlistProductCards = responses.join('');
    grid.innerHTML = wishlistProductCards;
    grid.classList.add(GRID_LOADED_CLASS);
    initButtons();
    return responses;
  } catch (error) {
    return Promise.reject(`Error setting up Grid: ${error}`);
  }
};

/**
 * Sets up wishlist buttons.
 *
 * @param {NodeList} buttons - The list of buttons to set up as wishlist buttons.
 * @returns {void}
 */
const setupButtons = (buttons) => {
  const wishlist = getWishlist();

  buttons.forEach((button) => {
    let { productHandle, variantId } = button.closest('.wishlist-container').dataset;

    //  set full icon when the product was previous added to wish list
    if ( isProductAlreadyOnWishlist(wishlist, {handle: productHandle, variantId: variantId}) ) {
      changeVisibilityButtons(button, 'show-full');
    }

    if (!productHandle || !variantId) {
      return console.error('WISHLIST: Missing `data-product-handle` or data-variant-id attribute. Failed to update the wishlist.');
    }

    button.addEventListener('click', e => {
      const wishListContainer = button.closest('.wishlist-container');
      const fullButton = wishListContainer && wishListContainer.querySelector('.wishlist-icon-full');
      updateWishlist(productHandle,variantId);
      if (document.querySelector(selectors.wishListContainer)) setupGrid();
      updateMenuIconHeader();
      if ( !fullButton.classList.contains(BUTTON_ACTIVE_CLASS) ) {
        changeVisibilityButtons(button, 'show-full');
      } else {
        changeVisibilityButtons(button, 'show-empty');
      }
    });
  });
};


/**
 * Initializes wishlist buttons and invoke setupButtons()
 *
 * @returns {void}
 */
const initButtons = (node = null) => {
  setTimeout(() => {
    const buttons = node ? node.querySelectorAll(selectors.button) : document.querySelectorAll(selectors.button);

    if (!buttons.length) return;
    setupButtons(buttons);
  },1000);
};

/**
 * Retrieves wishlist items from local storage.
 *
 * @returns {Array} - An array of wishlist items.
 */
const getWishlist = () => {
  const wishlist = JSON.parse(localStorage.getItem(LOCAL_STORAGE_WISHLIST_KEY));
  if (wishlist) return wishlist;
  return [];
};

/**
 * Set the wishlist items array on localstorage
 *
 * @returns {void}
 */
const setWishlist = (wishlist) => {
  if (wishlist.length) {
    localStorage.setItem(LOCAL_STORAGE_WISHLIST_KEY, JSON.stringify(wishlist));
  } else {
    localStorage.removeItem(LOCAL_STORAGE_WISHLIST_KEY);
  }

  return wishlist;
};

/**
 * Updates wishlist items in local storage.
 *
 * @param {string} handle - The handle of the product.
 * @param {string} variantId - The ID of the product variant.
 * @returns {Array} - The updated wishlist.
 */
const updateWishlist = (handle,variantId) => {
  const productInfo = {
    handle: handle,
    variantId: variantId
  };

  const wishlist = getWishlist();
  const indexInWishlist = wishlist.findIndex(product => product.handle === handle && product.variantId === variantId);
  if ( indexInWishlist === -1 ) {
    wishlist.push(productInfo)
  } else {
    wishlist.splice(indexInWishlist, 1);
  }
  return setWishlist(wishlist);

};

/**
 * Changes visibility of wishlist buttons.
 *
 * @param {HTMLElement} button - The wishlist button.
 * @param {string} action - The action to perform (either 'show-full' or 'show-empty').
 * @returns {void}
 */
const changeVisibilityButtons = (button, action) => {
  const wishListContainer = button.closest('.wishlist-container');
  const regularButton = wishListContainer && wishListContainer.querySelector('.wishlist-icon-empty');
  const hoverButton = wishListContainer && wishListContainer.querySelector('.wishlist-icon-hover');
  const fullButton = wishListContainer && wishListContainer.querySelector('.wishlist-icon-full');

  if ( action == "show-full" ) {
    regularButton.classList.add('wishlist-hidden');
    hoverButton.classList.add('full-active');
    fullButton.classList.add(BUTTON_ACTIVE_CLASS);
    fullButton.classList.remove('wishlist-hidden');
  } else {
    regularButton.classList.remove('wishlist-hidden');
    hoverButton.classList.remove('full-active');
    fullButton.classList.add('wishlist-hidden');
    fullButton.classList.remove(BUTTON_ACTIVE_CLASS);
  }
}

/**
 * Updates menu icon header based on wishlist items.
 *
 * @returns {void}
 */
const updateMenuIconHeader = () => {
  const menuIconEmpty = document.querySelector(selectors.menuIconEmpty);
  const menuIconFull = document.querySelector(selectors.menuIconFull);
  const wishList = getWishlist();
  if ( wishList.length ) {
    menuIconEmpty && menuIconEmpty.classList.add('wishlist-hidden');
    menuIconFull && menuIconFull.classList.remove('wishlist-hidden');
  } else {
    menuIconEmpty && menuIconEmpty.classList.remove('wishlist-hidden');
    menuIconFull && menuIconFull.classList.add('wishlist-hidden');
  }
}

/**
 * Redirects to wishlist page when menu icon header is clicked.
 *
 * @returns {void}
 */
const handleMenuIconHeader = () => {
  const iconsHeader = document.querySelectorAll(selectors.menuIconHeader);

  if ( iconsHeader ) {
    iconsHeader.forEach(iconHeader => {
      iconHeader.addEventListener('click', e => {
        window.location.href = '/pages/wishlist';
      });
    });
  }
}

/**
 * Checks if a product is already on the wishlist.
 *
 * @param {Array} wishlist - The wishlist.
 * @param {Object} productInfo - Information about the product.
 * @param {string} productInfo.handle - The handle of the product.
 * @param {string} productInfo.variantId - The ID of the product variant.
 * @returns {boolean} - True if the product is already on the wishlist, false otherwise.
 */
const isProductAlreadyOnWishlist = (wishlist, productInfo) => {
  const isProductAlreadyOnWishlist = wishlist.find(product => product.handle === productInfo.handle && product.variantId === productInfo.variantId);
  return !!isProductAlreadyOnWishlist;
}

/**
 * Show sizes when user clicks on ATC
 *
 * @returns {void}
 */
const showSizes = () => {
  let sizesContainer = '';
  const addToCartButtons = document.querySelectorAll(selectors.addToCartButtons);
  if ( addToCartButtons ) {
    addToCartButtons.forEach(addToCartButton => {
      addToCartButton.addEventListener('click', e => {
        const variantId = e.currentTarget.getAttribute("data-product-id");
        if ( isMobile ) {
          sizesContainer = document.querySelector(`#sizes-by-${variantId}:not(.redesign-plp-test) ul`);
        } else {
          sizesContainer = document.querySelector(`#sizes-by-${variantId} ul`);
        }

        sizesContainer.classList.toggle('open-sizes');

        const parentElement = addToCartButton.parentElement;
        if (parentElement) {
          parentElement.style.display = 'none';
        }
      });

    });
  }
}

/**
 * Process fetch to perform add to cart item
 *
 * @param {string} wishlist - The wishlist.
 * @param {number} productInfo - Information about the product.
 * @returns {void}
 */
const addToCart = (id,quantity) => {
  if (!id || !quantity) return false;

  let formData = {
    'items': [{
     'id': id,
     'quantity': quantity
     }]
   };

  const url = '/cart/add.js';

  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(formData)
  })
    .then(response => {
      if ( response.ok ) {
        theme.miniCart.updateElements()
        theme.miniCart.generateCart()
      }
    })
    .catch((err) => console.error(`Failed to Add to Cart, variant ID: ${id}`, err));
}

/**
 * Invoke the ATC function when a user clicks on a box size
 *
 * @returns {void}
 */
const handleBoxSizes = () => {
  const boxSizes = document.querySelectorAll(selectors.boxSizes);

  if ( boxSizes ) {
    boxSizes.forEach(boxSize => {
      boxSize.addEventListener('change', e => {
        let variantId = boxSize.value;
        addToCart(variantId, 1);
      });
    });
  }
}

/**
 * Invoke the ATC function when a user clicks on ATC button
 *
 * @returns {void}
 */
const handleNosizes = () => {
  const noSizes = document.querySelectorAll(selectors.noSizes);

  if ( noSizes ) {
    noSizes.forEach(noSize => {
      noSize.addEventListener('click', e => {
        let productId = noSize.getAttribute('pid');
        addToCart(productId, 1);
      });
    });
  }
}


/**
 * Observes for new product elements added to the Product Listing Page (PLP) via infinite scroll.
 */
function observeInfiniteScrollPLP () {
  const observer = new MutationObserver(function(mutations) {
    observer.disconnect();
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach((node) => {
          if ( node instanceof HTMLElement ) {
            initButtons(node);
          }
        });
      }
    });
    observer.observe(target, config);
  });
  let config = { childList: true, subtree: true };
  let target = document.querySelector('.AjaxinateLoop');
  if(!target) return;
  observer.observe(target, config);
}
